import { PageHero, Reveal, Chapter } from '../lib/motion';
import { CONTACT_EMAIL } from '../data/site';
import { useSEO } from '../lib/seo';

const sections = [
  {
    no: '01',
    label: 'What we collect',
    title: 'Only what a match requires.',
    paras: [
      'When you submit a form on Vox Meridian we collect the details you type in: company name, contact person, email, phone, and — for providers — your business registration number and service trades. Buyer RFQs additionally include your scope of work, budget range, deadline, and any files you choose to upload (drawings, specifications, photos).',
      'We also record simple, anonymous usage analytics (pages viewed, forms submitted) to understand what is working. That is the complete list.',
    ],
  },
  {
    no: '02',
    label: 'How it is used',
    title: 'One purpose: making the match.',
    paras: [
      'Your details are used to verify providers, to match buyer RFQs with a small number of suitable providers, and to contact you about your enquiry. Nothing else.',
      'We do not sell data, and we never share your details with third parties for marketing. We run no third-party advertising networks, trackers or sponsored placements.',
    ],
  },
  {
    no: '03',
    label: 'Who sees your details and drawings',
    title: 'The founder, then a handful of matched providers.',
    paras: [
      'Every submission goes first to the founder, who reviews it personally. If you are a buyer, your RFQ — including attached drawings or specifications — is then shared only with the three to five providers matched to quote on it. Matched providers are asked to treat your documents as confidential and to use them solely to prepare your quotation.',
      'RFQ details, drawings and contact information are never published on this site, listed in a public directory, or visible to other visitors.',
    ],
  },
  {
    no: '04',
    label: 'Confidential handling',
    title: 'Need an NDA-first workflow? Just say so.',
    paras: [
      `Technical drawings can be commercially sensitive. If you would like drawings withheld until you approve the provider shortlist, or you require a non-disclosure agreement before anything is shared, email ${CONTACT_EMAIL} before or after submitting your RFQ. During this concierge phase every request is handled by hand — confidential handling is simply noted on your RFQ and honoured.`,
    ],
  },
  {
    no: '05',
    label: 'Storage and your choices',
    title: 'Secured storage, deletion on request.',
    paras: [
      'Form details are stored in a secured database and uploaded files in private object storage; everything travels over HTTPS. Records are kept while the pilot phase runs so matches can be managed and followed up.',
      `You may ask at any time to see, correct, or delete the information we hold about you — email ${CONTACT_EMAIL} and it will be done. Vox Meridian is operated by its founder, Kumar, based in Malaysia, serving Singapore, Malaysia, the UAE, the United Kingdom, Australia, the United States, Canada, South Africa, India and Europe.`,
    ],
  },
  {
    no: '06',
    label: 'Public by choice',
    title: 'The Showcase is opt-in — always.',
    paras: [
      'Vox Meridian operates a public Showcase board where industrial service providers can display their company name, trade, country, tagline and business contact details. A listing appears only when a provider deliberately submits those details through the advertising form, and every listing is reviewed by the founder before it goes live. Nothing from a provider application, RFQ or email exchange is ever published automatically.',
      `Showcase listings never include RFQ history, quotations or any buyer information. If you hold a listing, you may have it updated or removed at any time by emailing ${CONTACT_EMAIL}.`,
    ],
  },
];

const Privacy = () => {
  useSEO({
    title: 'Privacy — How Your Details & Drawings Are Handled | Vox Meridian',
    description: 'How Vox Meridian handles your RFQ, drawings and contact details: founder review, only 3–5 matched providers, and opt-in public listings.',
    path: '/privacy',
  });

  return (
    <div data-testid="privacy-page">
      <PageHero
        eyebrow="Privacy"
        lines={[<>Plain English.</>, <span key="l2">No <span className="text-volt">fine print.</span></span>]}
        sub="How your details and technical drawings are handled on Vox Meridian — written by the founder who handles them, not by a lawyer."
      />
      <section className="bg-ink">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="max-w-3xl space-y-16">
            {sections.map((s) => (
              <div key={s.no} data-testid={`privacy-section-${s.no}`}>
                <Chapter no={s.no} label={s.label} title={s.title} />
                <div className="mt-8 space-y-5">
                  {s.paras.map((p, i) => (
                    <Reveal key={i} delay={i * 0.06}>
                      <p className="text-base leading-relaxed text-steel">{p}</p>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
            <Reveal>
              <div className="border border-line bg-surface p-8">
                <p className="mono-label text-volt">Questions or requests</p>
                <p className="mt-3 text-sm leading-relaxed text-steel">
                  Email <a href={`mailto:${CONTACT_EMAIL}`} data-testid="privacy-contact-link" className="text-parchment underline transition-colors duration-200 hover:text-volt">{CONTACT_EMAIL}</a> — the founder reads every message personally.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
